import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { MainLayout } from '../components/MainLayout';
import { useColorMode } from '../theme';
import { applySwaggerTheme } from './swagger-theme';

/**
 * Page that embeds the Swagger UI for FolioWorth API documentation.
 *
 * The backend already serves Swagger at /swagger/index.html. This page wraps it
 * in the main layout and restyles the embedded document to match the app —
 * see swagger-theme.ts for why the styles have to be injected rather than
 * declared, and why that is also what scopes them.
 */
const APIDocsPage: React.FC = () => {
  // Reason: Swagger UI lives at the server root (/swagger/index.html), not under
  // /api/v1. In production the frontend is served from the same origin as the API,
  // so we use a simple origin-relative path. In dev, Vite proxy forwards /swagger
  // to the backend.
  const swaggerUrl = '/swagger/index.html';

  const frameRef = useRef<HTMLIFrameElement>(null);
  const { mode } = useColorMode();
  // Same-origin is what makes the injection possible at all. If a deployment
  // ever splits the API onto another host, say so rather than showing a page
  // that looks broken for no visible reason.
  const [themed, setThemed] = useState(true);

  const theme = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    setThemed(applySwaggerTheme(frame));
  }, []);

  // Re-apply on a theme flip: the iframe does not inherit data-theme, so
  // nothing inside it changes on its own.
  useEffect(() => { theme(); }, [mode, theme]);

  const rule = { borderTop: '1px solid var(--border-soft)' };

  return (
    <MainLayout>
      <Box>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          flexWrap: 'wrap', gap: 2, pb: 2,
        }}>
          <Box>
            <Typography sx={{
              fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5,
            }}>
              API documentation
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)' }}>
              Every endpoint, generated from the server&rsquo;s own annotations.
              Authenticate with a personal access token —{' '}
              <Box component="code" className="fw-num" sx={{ fontSize: '0.875em' }}>
                Authorization: Bearer fw_…
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button component={Link} to="/api-tokens" variant="outlined" size="small">
              Manage tokens
            </Button>
            <Button
              href={swaggerUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
            >
              Open in a new tab
            </Button>
          </Box>
        </Box>

        {!themed && (
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--warn-text)', pb: 1 }}>
            The embedded reference is served from another origin, so it keeps its
            own styling.
          </Typography>
        )}

        {/* No card: a hairline rule and the page background, like the rest of
            the app's newer screens. */}
        <Box sx={{ ...rule, height: 'calc(100vh - 230px)', minHeight: 420 }}>
          <iframe
            ref={frameRef}
            src={swaggerUrl}
            onLoad={theme}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            title="FolioWorth API documentation"
          />
        </Box>
      </Box>
    </MainLayout>
  );
};

export default APIDocsPage;
